import { createContext, useContext, useMemo } from 'react';
import Swal from 'sweetalert2';

import { noop } from '@/helpers/noop';

export interface ConfirmationProperties {
  actionTitle: string;
  actionDescriptionText: string;
  actionButtonLabel: string;
  actionCompletedTitle: string;
  actionCompletedText: string;

  // no needed to check the type, can by any. "any" due to user don't need to transform the result to the value for Confirmation.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onConfirmPromise: () => Promise<any>;
}

function showConfirmation({
  actionTitle,
  actionDescriptionText,
  actionButtonLabel,
  actionCompletedTitle = '',
  actionCompletedText = '',
  onConfirmPromise,
}: ConfirmationProperties) {
  if (Swal.isVisible()) {
    return;
  }

  Swal.fire({
    icon: 'warning',
    title: actionTitle,
    text: actionDescriptionText,
    showCancelButton: true,
    confirmButtonText: actionButtonLabel,
    padding: '2em',
    customClass: { container: 'sweet-alerts' },
  })
    .then((result) => {
      if (result.value) {
        onConfirmPromise()
          .then(() => {
            Swal.fire({
              title: actionCompletedTitle,
              text: actionCompletedText,
              icon: 'success',
              customClass: { container: 'sweet-alerts' },
            }).catch((e) => {
              console.error(e);
            });
          })
          .catch((e) => {
            console.error(e);
          });
      }
    })
    .catch((e) => {
      console.error(e);
    });
}

type IModalContext = {
  /**
   * Show confirmation modal and calls provided function on confirmation.
   *
   * @example
   *  const modals = useModal();
   *  modals.showConfirmation(
   *    {
   *      actionButtonLabel: 'Button confirm',
   *      actionTitle: 'Confirm title',
   *      actionDescriptionText: 'Confirm desc',
   *      actionCompletedTitle: 'Completed',
   *      actionCompletedText: 'Completed desc',
   *      onConfirmPromise: (): Promise<boolean> => {
   *        return new Promise((resolve) => {
   *          resolve(true);
   *        });
   *      },
   *    }
   *  );
   */
  showConfirmation: (props: ConfirmationProperties) => void;
};

const initModalContext: IModalContext = {
  showConfirmation: noop,
};

const ModalContext = createContext(initModalContext);

export const ModalProvider: FCC = ({ children }) => (
  <ModalContext.Provider
    value={useMemo<IModalContext>(
      () => ({
        showConfirmation: showConfirmation,
      }),
      [],
    )}
  >
    {children}
  </ModalContext.Provider>
);

export const useModal = () => useContext(ModalContext);
